import { createApi } from "@reduxjs/toolkit/query/react";
// classApi owns the student-list queries (separate createApi instance), so attach/detach
// must invalidate ITS cache explicitly — cross-instance tag invalidation does not happen automatically.
import { classApi } from "@/api/class/classApi";
import {
  GetAssignmentAsManagerResponse as GetAssignmentAsManager,
  ManagerAssignmentEntity,
  GetAssignmentAsStudentResponse as GetAssignmentAsStudent,
  StudentAssignmentEntity,
} from "@/types/canonical";
import {
  AssignmentAttemptDetailRequestedModelFields,
  GetAssignmentActionsRequestedModelFields,
  GetAssignmentsAsManagerRequestedModelFields,
  ManagerAssignmentRequestedModelFields,
  ManagerAssignmentRequestedModelFieldsSafe,
  StudentAssignmentRequestedModelFields,
  WebinarSessionsRequestedModelFields,
  ManualProctoringRequestedModelFields,
  AttemptEnvironmentRequestedModelFields,
} from "./assignmentFields";
import {
  GetAssignmentsAsManagerResponse as GetAssignmentsAsManager,
  GetAssignmentsAsManagerParams,
  CreateAssignmentEntity,
  CreateAssignmentResponse,
  CreatedAssignmentEntity,
  UpdateAssignmentPayload,
  UpdateAssignmentResponse,
  UpdatedAssignmentEntity,
  AssignmentAttemptEntity,
  GetAssignmentAttemptDetailResponse as GetAssignmentAttemptDetail,
  GetAssignmentAttemptDetailParams,
  GetWebinarSessionsResponse,
  WebinarSessionPagination,
  ActionsResponse,
  StudentResultEntity,
  AttemptEnvironment,
  GetAttemptEnvironmentResponse,
} from "@/types/canonical";
import { privateBaseQuery } from "../base/private/privateBaseQuery";
import type { TaskEntity, TaskResponse } from "@/types/task";

const getRequestLocale = (): string => {
  if (typeof document === "undefined") return "en";
  const m = document.cookie.match(/(?:^|;\s*)NEXT_LOCALE=([^;]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : "en";
};

export interface GetAssignmentDetailParams {
  assignmentId: number;
  webinarId?: number;
  role?: string;
  /**
   * If true, uses a safe field set that avoids backend 500 on some assignment types
   * (e.g. `type=external` when requesting `max_points`).
   */
  safeFields?: boolean;
}

export type ProctoringStateValue = "enabled" | "disabled";

export type AssignmentExportRowValue =
  | "assignment"
  | "userid"
  | "fullname"
  | "email"
  | "username"
  | "phone"
  | "description"
  | "invite-code"
  | "results"
  | "points"
  | "score"
  | "answered"
  | "starting-at"
  | "ending-at"
  | "credibility"
  | "variant"
  | "violations"
  | "head_empty"
  | "head_many"
  | "head_depth"
  | "head_rotate"
  | "focus_down"
  | "copy_paste"
  | "extension_detected"
  | "screen"
  | "main-camera"
  | "second-camera"
  | "report-url";

export type AssignmentExportRows = Partial<Record<string, AssignmentExportRowValue>>;

export type AssignmentExportFormat = "xlsx" | "docx";

export type ExportAssignmentsPayload = {
  assignments: number[];
  rows: AssignmentExportRows;
  format?: AssignmentExportFormat;
  show_answers?: boolean;
  variant_group?: boolean;
  difficult_group?: boolean;
  only_started?: boolean;
  only_finished?: boolean;
  /** trust-rating.oqylyq.kz access token for the current user, used to fold analytics into the DOCX report. */
  trust_rating_token?: string;
};

export interface AssignmentManualSettingsEntity {
  main_camera_manual_disabled: boolean;
  second_camera_manual_disabled: boolean;
  screen_share_manual_disabled: boolean;
  head_identity_manual_disabled: boolean;
  head_tracking_manual_disabled: boolean;
  displays_check_manual_disabled: boolean;
  hdcp_check_manual_disabled: boolean;
  fullscreen_mode_manual_disabled: boolean;
  read_clipboard_manual_disabled: boolean;
  extension_detector_manual_disabled: boolean;
  noise_detector_manual_disabled: boolean;
  focus_detector_manual_disabled: boolean;
  speech_detector_manual_disabled: boolean;
  main_camera_manual_enabled: boolean;
  second_camera_manual_enabled: boolean;
  screen_share_manual_enabled: boolean;
  head_identity_manual_enabled: boolean;
  head_tracking_manual_enabled: boolean;
  displays_check_manual_enabled: boolean;
  hdcp_check_manual_enabled: boolean;
  fullscreen_mode_manual_enabled: boolean;
  read_clipboard_manual_enabled: boolean;
  extension_detector_manual_enabled: boolean;
  noise_detector_manual_enabled: boolean;
  focus_detector_manual_enabled: boolean;
  speech_detector_manual_enabled: boolean;
}

export interface AssignmentManualSettingsResponse {
  status: string;
  status_code: string;
  entity: AssignmentManualSettingsEntity;
}

export interface AssignmentActionsParams {
  assignmentId: number;
  page?: number;
  limit?: number;
  orderBy?: "asc" | "desc";
  context?: number;
  attemptId?: number;
  /** Только нарушения. Бэк отбирает их сам, фильтровать выдачу на клиенте не нужно. */
  isWarning?: boolean;
}

// Размер страницы и верхний потолок числа страниц при полной выгрузке
// событий попытки (см. getAllAssignmentActions). Потолок страхует от
// неадекватного last_page и ограничивает объём, попадающий в UI.
const ASSIGNMENT_ACTIONS_PAGE_LIMIT = 500;
const ASSIGNMENT_ACTIONS_MAX_PAGES = 40;

const buildAssignmentActionsRequest = ({
  assignmentId,
  page = 1,
  limit = 10,
  orderBy = "desc",
  context,
  attemptId,
  isWarning,
}: AssignmentActionsParams) => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    orderBy,
  });
  if (isWarning) params.append("is_warning", "1");
  if (context) params.append("context", String(context));
  if (attemptId) {
    // Prefer canonical name; keep legacy typo for backward compatibility.
    params.append("assignment_attempt_id", String(attemptId));
    params.append("assignment_attemtp_id", String(attemptId));
  }

  return {
    url: `/assignment/actions/${assignmentId}.json?${params.toString()}`,
    method: "GET" as const,
    headers: {
      "X-Requested-Fields": GetAssignmentActionsRequestedModelFields.join(","),
    },
  };
};

export const assignmentApi = createApi({
  reducerPath: "assignmentApi",
  baseQuery: privateBaseQuery(),
  tagTypes: ["AssignmentDetail", "ProctoringAssignment", "AssignmentsList", "AttemptDetail", "AttemptEnvironment", "Students", "AssignmentManualSettings"],
  endpoints: (builder) => ({
    getAssignmentsAsManager: builder.query<
      GetAssignmentsAsManager,
      GetAssignmentsAsManagerParams
    >({
      query: ({ limit, fields, ...params }) => ({
        url: "/assignments",
        method: "GET",
        params: {
          ...params,
          ...(typeof limit === "number"
            ? {
                limit,
                per_page: limit,
              }
            : {}),
        },
        headers: {
          "X-Requested-Fields": (
            fields ?? GetAssignmentsAsManagerRequestedModelFields
          ).join(","),
        },
      }),
      providesTags: ["AssignmentsList"],
    }),

    getAssignmentAsManager: builder.query<
      ManagerAssignmentEntity,
      GetAssignmentDetailParams
    >({
      query: ({ assignmentId, webinarId, safeFields }) => {
        const fields = safeFields
          ? ManagerAssignmentRequestedModelFieldsSafe
          : ManagerAssignmentRequestedModelFields;

        return {
          url: `/assignments/${assignmentId}`,
          method: "GET",
          params: webinarId ? { webinar_id: webinarId } : undefined,
          headers: {
            "X-Requested-Fields": fields.join(","),
          },
        };
      },
      transformResponse: (response: GetAssignmentAsManager) => response.entity,
      providesTags: (result, error, { assignmentId }) => [
        { type: "AssignmentDetail", id: assignmentId },
      ],
    }),

    getAssignmentAsStudent: builder.query<
      StudentAssignmentEntity,
      GetAssignmentDetailParams
    >({
      query: ({ assignmentId }) => {
        const fields = StudentAssignmentRequestedModelFields;

        return {
          url: `/assignments/${assignmentId}`,
          method: "GET",
          headers: {
            "X-Requested-Fields": fields.join(","),
          },
        };
      },
      transformResponse: (response: GetAssignmentAsStudent) => response.entity,
      providesTags: (result, error, { assignmentId }) => [
        { type: "AssignmentDetail", id: assignmentId },
      ],
    }),

    updateAssignment: builder.mutation<
      UpdatedAssignmentEntity,
      UpdateAssignmentPayload
    >({
      query: (payload) => ({
        url: `/assignments/${payload.id}`,
        method: "PUT",
        data: payload,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      transformResponse: (response: UpdateAssignmentResponse) =>
        response.entity,
      invalidatesTags: (result, error, { id }) => [
        { type: "AssignmentDetail", id },
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // class_id / participant changes made via a full assignment save (e.g.
          // clearing a group) land in a different RTK Query API (classApi) — same
          // cross-API invalidation as attachStudentsToAssignment/detachStudents below,
          // otherwise the participants list shows stale data until the next reload.
          dispatch(
            classApi.util.invalidateTags([
              { type: "Students", id: `external:${id}` },
              { type: "Students", id: `assignment:${id}` },
              { type: "Students", id: `class-in-assignment:${id}` },
            ])
          );
        } catch {
          /* mutation failed — nothing to refresh */
        }
      },
    }),

    createAssignment: builder.mutation<
      CreatedAssignmentEntity,
      CreateAssignmentEntity
    >({
      query: (newAssignment) => ({
        url: `/assignments`,
        method: "POST",
        data: newAssignment,
        headers: {
          "X-Requested-Fields": StudentAssignmentRequestedModelFields.join(","),
        },
      }),
      transformResponse: (response: CreateAssignmentResponse) =>
        response.entity,
      invalidatesTags: ["AssignmentDetail"],
    }),

    // Удалить задание
    deleteAssignment: builder.mutation<void, number>({
      query: (assignmentId) => ({
        url: `/assignments/${assignmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, assignmentId) => [
        { type: "AssignmentDetail", id: assignmentId },
        "AssignmentsList",
      ],
    }),

    getAssignmentAttemptDetail: builder.query<
      AssignmentAttemptEntity,
      GetAssignmentAttemptDetailParams
    >({
      query: ({ assignmentId, contextId, attemptId }) => ({
        url: `/assignments/${assignmentId}`,
        method: "GET",
        params: {
          context: contextId,
          assignment_attempt_id: attemptId,
        },
        headers: {
          "X-Requested-Fields":
            AssignmentAttemptDetailRequestedModelFields.join(","),
        },
      }),
      transformResponse: (response: GetAssignmentAttemptDetail) =>
        response.entity,
      providesTags: (result, error, { assignmentId, contextId }) => [
        { type: "AttemptDetail", id: `${assignmentId}-${contextId}` },
      ],
    }),

    getStudentIdsByActions: builder.query<
      number[],
      { assignmentId: number; actions: string[] }
    >({
      query: ({ assignmentId, actions }) => {
        const params = new URLSearchParams({
          assignment_id: String(assignmentId),
        });
        for (const action of actions) params.append("actions[]", action);

        return {
          url: `/assignment-attempts?${params.toString()}`,
          method: "GET",
          headers: { "X-Requested-Fields": "id,student_id" },
        };
      },
      transformResponse: (response: { entities?: { student_id?: number }[] }) =>
        Array.from(
          new Set(
            (response.entities ?? [])
              .map((item) => item.student_id)
              .filter((id): id is number => typeof id === "number")
          )
        ),
    }),

    getAttemptEnvironment: builder.query<AttemptEnvironment | null, number>({
      query: (attemptId) => ({
        url: "/assignment-attempts",
        method: "GET",
        params: { id: attemptId },
        headers: {
          "X-Requested-Fields": AttemptEnvironmentRequestedModelFields.join(","),
        },
      }),
      transformResponse: (response: GetAttemptEnvironmentResponse) =>
        response.entities?.[0]?.environment ?? null,
      providesTags: (result, error, attemptId) => [
        { type: "AttemptEnvironment", id: attemptId },
      ],
    }),

    getAssignmentManualSettings: builder.query<
      AssignmentManualSettingsEntity,
      { assignmentId: number; contextId: number }
    >({
      query: ({ assignmentId, contextId }) => ({
        url: `/assignments/${assignmentId}`,
        method: "GET",
        params: {
          context: contextId,
        },
        headers: {
          "X-Requested-Fields":
            ManualProctoringRequestedModelFields.join(","),
        },
      }),
      transformResponse: (response: AssignmentManualSettingsResponse) =>
        response.entity,
      providesTags: (result, error, { assignmentId, contextId }) => [
        { type: "AssignmentManualSettings", id: `${assignmentId}-${contextId}` },
      ],
    }),

    getWebinarSessions: builder.query<
      WebinarSessionPagination,
      { assignmentId: number; studentId: number }
    >({
      query: ({ assignmentId, studentId }) => ({
        url: `webinar-sessions`,
        method: "GET",
        params: {
          assignment_id: assignmentId,
          student_id: studentId,
          limit: 99,
        },
        headers: {
          "X-Requested-Fields": WebinarSessionsRequestedModelFields.join(","),
        },
      }),
      transformResponse: (response: GetWebinarSessionsResponse) =>
        response.entities,
    }),

    getAssignmentActions: builder.query<
      ActionsResponse["entities"],
      AssignmentActionsParams
    >({
      query: buildAssignmentActionsRequest,
      transformResponse: (response: ActionsResponse) => response.entities,
    }),

    /**
     * Полная выгрузка событий попытки: бэк отдаёт логи страницами, поэтому
     * одиночный запрос обрезает хвост длинного экзамена (часть меток просто
     * не доезжает до таймлайна). Здесь страницы дочитываются до конца.
     */
    getAllAssignmentActions: builder.query<
      ActionsResponse["entities"]["data"],
      Omit<AssignmentActionsParams, "page" | "limit">
    >({
      async queryFn(params, _api, _extra, baseQuery) {
        const fetchPage = (page: number) =>
          baseQuery(
            buildAssignmentActionsRequest({
              ...params,
              page,
              limit: ASSIGNMENT_ACTIONS_PAGE_LIMIT,
            })
          );

        const entitiesOf = (response: { data?: unknown }) =>
          (response.data as ActionsResponse | undefined)?.entities;

        const firstResponse = await fetchPage(1);
        if (firstResponse.error) return { error: firstResponse.error };

        const firstEntities = entitiesOf(firstResponse);
        const pages = [firstEntities?.data ?? []];

        // Остальные страницы тянем параллельно: последовательная догрузка
        // заметно задерживала открытие редактора на длинном экзамене.
        const lastPage = Math.min(
          firstEntities?.last_page ?? 1,
          ASSIGNMENT_ACTIONS_MAX_PAGES
        );
        if (lastPage > 1) {
          const rest = await Promise.all(
            Array.from({ length: lastPage - 1 }, (_, i) => fetchPage(i + 2))
          );
          // Упавшую страницу пропускаем: неполный таймлайн лучше пустого.
          for (const response of rest) {
            if (!response.error) pages.push(entitiesOf(response)?.data ?? []);
          }
        }

        // Дедуп по id: во время выгрузки на активном экзамене может прилететь
        // новое событие и сдвинуть пагинацию, продублировав часть страницы.
        const collected = new Map<
          ActionsResponse["entities"]["data"][number]["id"],
          ActionsResponse["entities"]["data"][number]
        >();
        for (const pageItems of pages) {
          for (const item of pageItems) collected.set(item.id, item);
        }

        return { data: Array.from(collected.values()) };
      },
    }),

    /**
     * Violations counter for one attempt.
     *
     * The students list (`/students`) has no violations field: `violations.total`
     * lives on the assignment endpoint and needs `_student_context` /
     * `_attempt_context`, which only `/assignments/{id}` sets. So we ask the
     * actions endpoint for one warning row and read the paginator total.
     */
    getAttemptViolationsTotal: builder.query<
      number,
      { assignmentId: number; studentId: number; attemptId: number }
    >({
      query: ({ assignmentId, studentId, attemptId }) => {
        const params = new URLSearchParams({
          context: String(studentId),
          assignment_attempt_id: String(attemptId),
          is_warning: "1",
          limit: "1",
        });

        return {
          url: `/assignment/actions/${assignmentId}.json?${params.toString()}`,
          method: "GET",
          headers: {
            "X-Requested-Fields": "id",
          },
        };
      },
      transformResponse: (response: ActionsResponse) =>
        response.entities?.total ?? 0,
    }),

    // Изменить proctoring-state (manual_* enabled/disabled)
    updateAssignmentProctoringState: builder.mutation<
      { status: string; status_code: string },
      {
        assignmentId: number;
        studentId: number;
        action: string;
        state: ProctoringStateValue;
      }
    >({
      query: ({ assignmentId, studentId, action, state }) => ({
        url: `/assignment/actions/${assignmentId}/proctoring-states.json`,
        method: "POST",
        data: {
          student_id: studentId,
          action,
          state,
        },
      }),
      invalidatesTags: (result, error, { assignmentId, studentId }) => [
        { type: "AssignmentManualSettings", id: `${assignmentId}-${studentId}` },
      ],
    }),

    // Приостановить экзамен
    suspendAssignment: builder.mutation<
      ManagerAssignmentEntity,
      { assignmentId: number }
    >({
      query: ({ assignmentId }) => ({
        url: `/assignment/suspend/${assignmentId}.json`,
        method: "PATCH",
      }),
      transformResponse: (response: GetAssignmentAsManager) => response.entity,
      invalidatesTags: (result, error, { assignmentId }) => [
        { type: "AssignmentDetail", id: assignmentId },
      ],
    }),

    // Возобновить экзамен
    resumeAssignment: builder.mutation<
      ManagerAssignmentEntity,
      { assignmentId: number }
    >({
      query: ({ assignmentId }) => ({
        url: `/assignment/resume/${assignmentId}.json`,
        method: "PATCH",
        data: {
          status: "completed",
        },
      }),
      transformResponse: (response: GetAssignmentAsManager) => response.entity,
      invalidatesTags: (result, error, { assignmentId }) => [
        { type: "AssignmentDetail", id: assignmentId },
      ],
    }),

    // Завершить экзамен
    closeAssignment: builder.mutation<
      ManagerAssignmentEntity,
      { assignmentId: number }
    >({
      query: ({ assignmentId }) => ({
        url: `/assignment/close/${assignmentId}.json`,
        method: "PATCH",
      }),
      transformResponse: (response: GetAssignmentAsManager) => response.entity,
      invalidatesTags: (result, error, { assignmentId }) => [
        { type: "AssignmentDetail", id: assignmentId },
        "AssignmentsList",
      ],
    }),

    // Начать новую попытку
    startNewAttempt: builder.mutation<
      { id: number; assignment_id: number; student_id: number; attempt: number | null; status: string; state: string | null; variant: number | null; points: number; credibility: number | null; spending_time: number | null },
      { assignmentId: number }
    >({
      query: ({ assignmentId }) => ({
        url: `/assignment/attempts/${assignmentId}/start.json`,
        method: "POST",
      }),
      transformResponse: (response: { status: string; status_code: string; entity: any }) => response.entity,
      invalidatesTags: (result, error, { assignmentId }) => [
        { type: "AssignmentDetail", id: assignmentId },
      ],
    }),

    retryAssignQuestions: builder.mutation<
      { count: number },
      { assignmentId: number }
    >({
      query: ({ assignmentId }) => ({
        url: `/assignment/attempts/${assignmentId}/retry-assign-questions.json`,
        method: "POST",
      }),
      transformResponse: (response: { entity: { count: number } }) => response.entity,
    }),

    createAssignmentAction: builder.mutation<
      { status: string; status_code: string },
      { assignmentId: number; actions: any[] }
    >({
      query: ({ assignmentId, actions }) => ({
        url: `/assignment/actions/${assignmentId}.json`,
        method: "POST",
        data: { actions },
      }),
    }),

    startAssignmentAction: builder.mutation<
      { success: boolean },
      { assignmentId: number }
    >({
      query: ({ assignmentId }) => ({
        url: `/assignment/actions/${assignmentId}/start.json`,
        method: "POST",
      }),
    }),

    finishAssignmentAction: builder.mutation<
      { success: boolean },
      { assignmentId: number }
    >({
      query: ({ assignmentId }) => ({
        url: `/assignment/actions/${assignmentId}/finish.json`,
        method: "POST",
      }),
    }),

    // Обновить результат по конкретному компоненту (ручная оценка)
    updateAssignmentResult: builder.mutation<
      { status: string; status_code: string },
      StudentResultEntity
    >({
      query: (payload) => ({
        url: `/assignment-results/${payload.id}`,
        method: "PUT",
        data: payload,
      }),
    }),

    // Сгенерировать ссылку отчёта для конкретного студента
    generateAssignmentReportLink: builder.mutation<
      any,
      { assignmentId: number | string; studentId: number | string }
    >({
      query: ({ assignmentId, studentId }) => ({
        url: `/assignment/reports/${assignmentId}/${studentId}.json`,
        method: "POST",
        data: {},
      }),
    }),

    // Изменение доступного времени попытки (аналог changeTimeStatesApiUrl)
    changeAssignmentTimeState: builder.mutation<
      { status: string; status_code: string },
      {
        assignmentId: number | string;
        studentId: number | string;
        state: "increase" | "decrease" | "reset";
        minutes?: number;
      }
    >({
      query: ({ assignmentId, studentId, state, minutes }) => ({
        url: `/assignment/actions/${assignmentId}/time-states.json`,
        method: "POST",
        data: {
          student_id: studentId,
          state,
          ...(typeof minutes === "number" ? { minutes } : {}),
        },
      }),
      invalidatesTags: (result, error, { assignmentId, studentId }) => [
        { type: "AttemptDetail", id: `${assignmentId}-${studentId}` },
      ],
    }),

    // Изменение состояния задания для конкретного студента (finish / restore)
    changeAssignmentStudentState: builder.mutation<
      { status: string; status_code: string },
      {
        assignmentId: number | string;
        studentId: number | string;
        state: "finish" | "restore";
      }
    >({
      query: ({ assignmentId, studentId, state }) => ({
        url: `/assignment/actions/${assignmentId}/assignment-states.json`,
        method: "POST",
        data: {
          student_id: studentId,
          state,
        },
      }),
      invalidatesTags: (result, error, { assignmentId, studentId }) => [
        { type: "AttemptDetail", id: `${assignmentId}-${studentId}` },
      ],
    }),

    /**
     * 5.4. Отвязать нескольких студентов
     *
     *  { "students": [123, 124, 125] }
     */
    detachStudents: builder.mutation<
      { status: string; status_code: string },
      { assignmentId: number; studentIds: number[] }
    >({
      query: ({ assignmentId, studentIds }) => ({
        url: `/assignment/${assignmentId}/detach-students.json`,
        method: "POST",
        data: {
          students: studentIds,
        },
        headers: {
          "Content-Type": "application/json",
        },
      }),
      // Счётчик участников в списке экзаменов приходит из того же ответа, что и сам
      // список заданий, поэтому его кеш тоже надо сбросить.
      invalidatesTags: (result, error, { assignmentId }) => [
        { type: "AssignmentDetail", id: assignmentId },
        "AssignmentsList",
      ],
      async onQueryStarted({ assignmentId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            classApi.util.invalidateTags([
              { type: "Students", id: `external:${assignmentId}` },
              { type: "Students", id: `assignment:${assignmentId}` },
              { type: "Students", id: `class-in-assignment:${assignmentId}` },
            ])
          );
        } catch {
          /* mutation failed — nothing to refresh */
        }
      },
    }),

    /**
     * Прикрепить студентов к заданию (массив)
     * POST /api/assignment/:id/attach-students.json
     * { "students": [123, 456, 789] }
     */
    attachStudentsToAssignment: builder.mutation<
      { status: string; status_code: string },
      { assignmentId: number; studentIds: number[] }
    >({
      query: ({ assignmentId, studentIds }) => ({
        url: `/assignment/${assignmentId}/attach-students.json`,
        method: "POST",
        data: {
          students: studentIds,
        },
        headers: {
          "Content-Type": "application/json",
        },
      }),
      // Счётчик участников в списке экзаменов приходит из того же ответа, что и сам
      // список заданий, поэтому его кеш тоже надо сбросить.
      invalidatesTags: (result, error, { assignmentId }) => [
        { type: "AssignmentDetail", id: assignmentId },
        "AssignmentsList",
      ],
      async onQueryStarted({ assignmentId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            classApi.util.invalidateTags([
              { type: "Students", id: `external:${assignmentId}` },
              { type: "Students", id: `assignment:${assignmentId}` },
              { type: "Students", id: `class-in-assignment:${assignmentId}` },
            ])
          );
        } catch {
          /* mutation failed — nothing to refresh */
        }
      },
    }),

    saveQuizAnswer: builder.mutation<
      { status: string; status_code: string; result?: StudentResultEntity },
      {
        assignmentId: number;
        attemptId: number;
        componentId: number;
        freeQuestionId: number;
        optionId: number | number[];
      }
    >({
      query: ({ assignmentId, componentId, attemptId, freeQuestionId, optionId }) => {
        const optionIds = Array.isArray(optionId) ? optionId : [optionId];

        return {
          url: `/assignment/quiz-attempts/${assignmentId}/${componentId}.json`,
          method: "POST",
          data: optionIds.map(id => ({
            assignment_id: assignmentId,
            assignment_attempt_id: attemptId,
            free_question_id: freeQuestionId,
            option_id: id,
            id: null,
            student_id: null,
            result: null,
          })),
        };
      },
    }),

    uploadAttachment: builder.mutation<
      { id: string; url: string; name: string; size: number },
      { file: File; storage?: string }
    >({
      query: ({ file, storage = 'default' }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('storage', storage);
        formData.append('extended', 'true');

        return {
          url: `/upload.json?extended=true`,
          method: 'POST',
          data: formData,
        };
      },
      transformResponse: (response: {
        status: string;
        status_code: string;
        file_url: string;
        file_name: string;
        file_size: number;
      }) => ({
        id: Date.now().toString(),
        url: response.file_url,
        name: response.file_name,
        size: response.file_size,
      }),
    }),

    createExternalUploadSession: builder.mutation<
      { qrcode_link: string; qrcode_image: string },
      { channel: string }
    >({
      query: ({ channel }) => ({
        url: '/external-session/upload.json',
        method: 'POST',
        data: {
          channel,
          storage: null,
        },
      }),
      transformResponse: (response: { status: string; status_code: string; qrcode_link: string; qrcode_image: string }) => ({
        qrcode_link: response.qrcode_link,
        qrcode_image: response.qrcode_image,
      }),
    }),

    saveOpenQuestionAnswer: builder.mutation<
      { status: string; status_code: string; result?: StudentResultEntity },
      {
        assignmentId: number;
        attemptId: number;
        componentId: number;
        answer: string;
        attachments?: Array<{ url: string; name: string; size: number }>;
      }
    >({
      query: ({ assignmentId, componentId, attemptId, answer, attachments = [] }) => ({
        url: `/assignment/quiz-attempts/${assignmentId}/${componentId}.json`,
        method: 'POST',
        data: {
          id: null,
          question_id: null,
          student_id: null,
          assignment_attempt_id: attemptId,
          antiplagiarism_task_id: null,
          answer,
          result: null,
          antiplagiarism_task: {
            REQUEST_CONTINUE: 0,
            REQUEST_REDUNDANT: 1,
            REQUEST_SKIP: 2,
            id: null,
            antiplagiarism_service_id: null,
            author_id: null,
            state: null,
            uid: null,
            text: null,
            file_url: null,
            file_name: null,
            file_size: null,
            mime_type: null,
            name: null,
            description: null,
            unique: null,
            references: [],
            report_url: null,
            failure_state_code: null,
            failure_state_message: null,
          },
          attachments,
        },
      }),
    }),

    saveCodeQuestionAnswer: builder.mutation<
      { status: string; status_code: string; result?: StudentResultEntity },
      {
        assignmentId: number;
        attemptId: number;
        componentId: number;
        codeQuestionId?: number;
        languageId: number;
        code: string;
        attemptPayload?: Record<string, unknown>;
        arguments?: Array<Record<string, unknown>>;
        argumentIds?: Array<{ code_question_argument_id: number }>;
      }
    >({
      query: ({
        assignmentId,
        componentId,
        attemptId,
        codeQuestionId,
        languageId,
        code,
        attemptPayload,
        arguments: fullArguments,
        argumentIds,
      }) => {
        const data: Record<string, unknown> = {
          ...(attemptPayload ?? {}),
          assignment_id: assignmentId,
          assignment_attempt_id: attemptId,
          ...(typeof codeQuestionId === "number" ? { code_question_id: codeQuestionId } : {}),
          code_execution_language_id: languageId,
          answer: code,
        };

        if (Array.isArray(fullArguments) && fullArguments.length > 0) {
          data["arguments"] = fullArguments;
        } else if (Array.isArray(argumentIds) && argumentIds.length > 0) {
          data["arguments"] = argumentIds;
        }

        return {
          url: `/assignment/quiz-attempts/${assignmentId}/${componentId}.json`,
          method: "POST",
          data,
        };
      },
    }),

    saveFillSpaceQuestionAnswer: builder.mutation<
      { status: string; status_code: string; result?: StudentResultEntity },
      {
        assignmentId: number;
        attemptId: number;
        componentId: number;
        answers: Array<{
          fillSpaceQuestionId: number;
          optionId: number;
          answer: string;
          option: any;
        }>;
      }
    >({
      query: ({ assignmentId, componentId, attemptId, answers }) => ({
        url: `/assignment/quiz-attempts/${assignmentId}/${componentId}.json`,
        method: 'POST',
        data: answers.map(item => ({
          assignment_id: assignmentId,
          assignment_attempt_id: attemptId,
          fill_space_question_id: item.fillSpaceQuestionId,
          option_id: item.optionId,
          answer: item.answer,
          option: item.option,
          id: null,
          student_id: null,
          result: null,
        })),
      }),
    }),

    saveDragAndDropQuestionAnswer: builder.mutation<
      { status: string; status_code: string; result?: StudentResultEntity },
      {
        assignmentId: number;
        attemptId: number;
        componentId: number;
        dragAndDropQuestionId: number;
        assignments: Array<{
          optionId: string | number;
          zoneId: string | number;
        }>;
      }
    >({
      query: ({
        assignmentId,
        componentId,
        attemptId,
        dragAndDropQuestionId,
        assignments,
      }) => ({
        url: `/assignment/quiz-attempts/${assignmentId}/${componentId}.json`,
        method: "POST",
        data: assignments.map((a) => ({
          assignment_id: assignmentId,
          assignment_attempt_id: attemptId,
          drag_n_drop_question_id: dragAndDropQuestionId,
          option_id: a.optionId,
          zone_id: a.zoneId,
          id: null,
          student_id: null,
          result: null,
        })),
      }),
    }),

    requestPasswordResetViaCode: builder.mutation<
      { status: string; status_code: string; verification_type: string },
      { username: string }
    >({
      query: ({ username }) => ({
        url: `/password/request-via-code.json`,
        method: "POST",
        data: { username },
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),

    downloadCertificate: builder.mutation<Blob, { assignmentId: number }>({
      query: ({ assignmentId }) => ({
        url: `/assignment/certificate/${assignmentId}.pdf`,
        method: 'POST',
        data: {},
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf',
        },
      }),
    }),

    /**
     * Export assignment report (XLSX) for one or more assignments.
     * Backend: POST /api/assignment/export.json
     */
    createAssignmentExportTask: builder.mutation<TaskEntity, ExportAssignmentsPayload>({
      query: (payload) => ({
        url: `/assignment/export.json`,
        method: "POST",
        data: payload,
        timeout: 9000000,
        headers: {
          Accept: "application/json, text/plain, */*",
          locale: getRequestLocale(),
        },
      }),
      transformResponse: (response: TaskResponse) => response.entity,
    }),
  }),
});

export const {
  useGetAssignmentsAsManagerQuery,
  useUpdateAssignmentMutation,
  useCreateAssignmentMutation,
  useDeleteAssignmentMutation,
  useGetAssignmentAsManagerQuery,
  useGetAssignmentAsStudentQuery,
  useGetAssignmentAttemptDetailQuery,
  useGetAssignmentManualSettingsQuery,
  useGetAttemptEnvironmentQuery,
  useGetStudentIdsByActionsQuery,
  useGetWebinarSessionsQuery,
  useGetAssignmentActionsQuery,
  useGetAllAssignmentActionsQuery,
  useGetAttemptViolationsTotalQuery,
  useUpdateAssignmentProctoringStateMutation,
  useSuspendAssignmentMutation,
  useResumeAssignmentMutation,
  useCloseAssignmentMutation,
  useStartNewAttemptMutation,
  useRetryAssignQuestionsMutation,
  useCreateAssignmentActionMutation,
  useStartAssignmentActionMutation,
  useFinishAssignmentActionMutation,
  useUpdateAssignmentResultMutation,
  useGenerateAssignmentReportLinkMutation,
  useChangeAssignmentTimeStateMutation,
  useChangeAssignmentStudentStateMutation,
  useDetachStudentsMutation,
  useAttachStudentsToAssignmentMutation,
  useSaveQuizAnswerMutation,
  useUploadAttachmentMutation,
  useCreateExternalUploadSessionMutation,
  useSaveOpenQuestionAnswerMutation,
  useSaveCodeQuestionAnswerMutation,
  useSaveFillSpaceQuestionAnswerMutation,
  useSaveDragAndDropQuestionAnswerMutation,
  useRequestPasswordResetViaCodeMutation,
  useDownloadCertificateMutation,
  useCreateAssignmentExportTaskMutation,
} = assignmentApi;
